import React, { useCallback, useMemo, useState } from 'react';
import { FileWithPath, useDropzone } from 'react-dropzone';
import { Download } from '@mui/icons-material';
import { CircularProgress } from '@mui/material';
import {
  ENTITY_RELATIONSHIP_TYPES,
  formatEntityRelationshipTypeLabel,
  downloadRelationshipCSVTemplate,
  parseRelationshipCSV,
  ParsedRelationshipRow,
  RELATIONSHIP_BULK_UPLOAD_MAX_ROWS,
  RELATIONSHIP_SOURCE_LINK_HEADER,
  RELATIONSHIP_SOURCE_BUSINESS_HEADER,
  RELATIONSHIP_SOURCE_PERSON_HEADER,
  RELATIONSHIP_TARGET_LINK_HEADER,
  RELATIONSHIP_TARGET_BUSINESS_HEADER,
  RELATIONSHIP_TARGET_PERSON_HEADER,
  RELATIONSHIP_TYPE_HEADER,
} from '../../../utils/biteDbRelationshipTemplates';

const chipStyle: React.CSSProperties = {
  padding: '4px 8px',
  borderRadius: '999px',
  border: '1px solid var(--secondary-color)',
  background: 'var(--input-color)',
  color: 'var(--text-primary)',
  fontSize: '11px',
  fontWeight: 500,
  lineHeight: 1.2,
};

type EntityRelationshipImportStepProps = {
  onRelationshipsParsed: (rows: ParsedRelationshipRow[]) => void;
  onDoLater?: () => void;
  isUploading?: boolean;
};

const EntityRelationshipImportStep: React.FC<EntityRelationshipImportStepProps> = ({
  onRelationshipsParsed,
  onDoLater,
  isUploading = false,
}) => {
  const [file, setFile] = useState<File | null>(null);
  const [parseErrors, setParseErrors] = useState<string[]>([]);
  const [parsedCount, setParsedCount] = useState(0);
  const [isParsing, setIsParsing] = useState(false);

  const handleDownloadTemplate = useCallback(() => {
    downloadRelationshipCSVTemplate('relationship_template.csv');
  }, []);

  const handleFileParsed = useCallback(
    async (selectedFile: File) => {
      setIsParsing(true);
      setParseErrors([]);
      setParsedCount(0);
      try {
        const { rows, errors } = await parseRelationshipCSV(selectedFile);
        setParseErrors(errors);
        setParsedCount(rows.length);
        onRelationshipsParsed(rows);
      } catch (err: any) {
        setParseErrors([err.message || 'Failed to parse file']);
        onRelationshipsParsed([]);
      } finally {
        setIsParsing(false);
      }
    },
    [onRelationshipsParsed],
  );

  const onDrop = useCallback(
    async (acceptedFiles: FileWithPath[]) => {
      const selectedFile = acceptedFiles[0];
      if (!selectedFile) return;
      setFile(selectedFile as unknown as File);
      await handleFileParsed(selectedFile as unknown as File);
    },
    [handleFileParsed],
  );

  const handleRemoveFile = useCallback(() => {
    setFile(null);
    setParseErrors([]);
    setParsedCount(0);
    onRelationshipsParsed([]);
  }, [onRelationshipsParsed]);

  const { getRootProps, getInputProps, isDragActive, isDragAccept, isDragReject } =
    useDropzone({
      onDrop: onDrop,
      accept: ['.csv', '.xlsx'],
      multiple: false,
    });

  const dropzoneClassName = useMemo(() => {
    const baseClass = 'dropzone-base';
    if (isDragAccept) return `${baseClass} dropzone-accept`;
    if (isDragReject) return `${baseClass} dropzone-reject`;
    if (isDragActive) return `${baseClass} dropzone-active`;
    return file ? `${baseClass} dropzone-file-present` : `${baseClass} dropzone-default`;
  }, [isDragActive, isDragAccept, isDragReject, file]);

  return (
    <div
      className='modal-section'
      style={{ width: '100%', alignSelf: 'stretch', boxSizing: 'border-box' }}
    >
      <h3 className='modal-section-title'>3. Define Relationships (Optional)</h3>

      <button
        onClick={handleDownloadTemplate}
        className='button button-primary button-full-width'
        type='button'
      >
        <Download style={{ marginRight: '8px' }} /> Download Relationship CSV
      </button>

      <p className='modal-section-subtitle' style={{ marginTop: '8px' }}>
        Upload a CSV or XLSX with columns <code>{RELATIONSHIP_SOURCE_LINK_HEADER}</code>,{' '}
        <code>{RELATIONSHIP_SOURCE_BUSINESS_HEADER}</code>, <code>{RELATIONSHIP_SOURCE_PERSON_HEADER}</code>,{' '}
        <code>{RELATIONSHIP_TARGET_LINK_HEADER}</code>, <code>{RELATIONSHIP_TARGET_BUSINESS_HEADER}</code>,{' '}
        <code>{RELATIONSHIP_TARGET_PERSON_HEADER}</code>, and{' '}
        <code>{RELATIONSHIP_TYPE_HEADER}</code>. Provide exactly one identifier per side and
        upload up to {RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()} relationships per
        file. You can use the system-generated <code>link_id</code> or the existing
        business/person identifiers. The <code>relationship_type</code> describes the target
        relative to the source: use company to person for roles like <code>ceo</code>, and
        company to company for types like <code>subsidiary</code>. For <code>parent_company</code>,
        the source is the child company and the target is the parent company.
      </p>

      <div {...getRootProps()} className={dropzoneClassName} style={{ marginTop: '12px' }}>
        <input {...getInputProps()} />
        {file ? (
          <div className='dropzone-content-item'>
            {isParsing ? (
              <CircularProgress size={20} style={{ color: 'var(--text-primary)' }} />
            ) : (
              <svg
                className='dropzone-icon-success'
                xmlns='http://www.w3.org/2000/svg'
                fill='none'
                viewBox='0 0 24 24'
                strokeWidth={1.5}
                stroke='currentColor'
              >
                <path
                  strokeLinecap='round'
                  strokeLinejoin='round'
                  d='M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z'
                />
              </svg>
            )}
            <p className='dropzone-filename'>{file.name}</p>
            <p className='dropzone-filesize'>
              {parsedCount > 0 ? `${parsedCount} relationships found` : 'No valid relationships yet'}
            </p>
            <button
              onClick={(e) => {
                e.stopPropagation();
                handleRemoveFile();
              }}
              className='dropzone-remove-button'
            >
              Remove File
            </button>
          </div>
        ) : (
          <div className='dropzone-content-item'>
            <p className='dropzone-text'>
              <span className='dropzone-text-highlight'>Click to upload</span> or drag and drop
            </p>
            <p className='dropzone-subtext'>
              Relationship CSV or XLSX, up to {RELATIONSHIP_BULK_UPLOAD_MAX_ROWS.toLocaleString()} rows
            </p>
          </div>
        )}
      </div>

      {parseErrors.length > 0 && (
        <div
          style={{
            marginTop: '8px',
            width: '100%',
            padding: '10px 12px',
            boxSizing: 'border-box',
            borderRadius: '8px',
            border: '1px solid #ef4444',
            background: 'rgba(239, 68, 68, 0.08)',
            fontSize: '12px',
            color: '#ef4444',
            maxHeight: '120px',
            overflowY: 'auto',
          }}
        >
          {parseErrors.map((err, i) => (
            <div key={i}>{err}</div>
          ))}
        </div>
      )}

      <div
        style={{
          marginTop: '12px',
          padding: '12px',
          borderRadius: '10px',
          border: '1px solid var(--secondary-color)',
          background: 'var(--input-color)',
        }}
      >
        <div
          style={{
            color: 'var(--text-primary)',
            fontSize: '13px',
            fontWeight: 600,
            marginBottom: '6px',
          }}
        >
          Valid relationship types
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
          {ENTITY_RELATIONSHIP_TYPES.map((type) => (
            <span key={type} style={chipStyle}>
              {formatEntityRelationshipTypeLabel(type)}
            </span>
          ))}
        </div>
      </div>
      {onDoLater && (
        <button
          onClick={onDoLater}
          className='button button-secondary button-full-width'
          type='button'
          style={{ marginTop: '12px' }}
          disabled={isUploading}
        >
          {isUploading ? 'Uploading Relationships...' : "I'll Add Relationships Later"}
        </button>
      )}
    </div>
  );
};

export default EntityRelationshipImportStep;

